import React, { useState, useEffect } from 'react';
import { accountingAPI } from '../services/api';
import { toast } from 'sonner';
import { Calendar, Lock, Unlock, Plus, Shield, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function FinancialControls() {
  const [years, setYears] = useState([]);
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [newFY, setNewFY] = useState({ start_date: '', end_date: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [fy, ml] = await Promise.all([accountingAPI.financialYears(), accountingAPI.monthLocks()]);
      setYears(fy.data);
      setLocks(ml.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreateFY = async () => {
    if (!newFY.start_date || !newFY.end_date) { toast.error('Both dates required'); return; }
    try {
      await accountingAPI.createFinancialYear(newFY);
      toast.success('Financial year created');
      setCreateDialog(false);
      setNewFY({ start_date: '', end_date: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCloseFY = async (fy) => {
    if (!window.confirm(`Close ${fy.name}? All vouchers in this period will be LOCKED.`)) return;
    try {
      await accountingAPI.closeFinancialYear(fy.fy_id);
      toast.success(`${fy.name} closed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleReopenFY = async (fy) => {
    try {
      await accountingAPI.reopenFinancialYear(fy.fy_id);
      toast.success(`${fy.name} reopened`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const lockMap = {};
  locks.forEach(l => { lockMap[l.month] = l.is_locked; });

  const toggleMonth = async (month) => {
    try {
      if (lockMap[month]) {
        await accountingAPI.unlockMonth(month);
        toast.success(`${month} unlocked`);
      } else {
        await accountingAPI.lockMonth(month);
        toast.success(`${month} locked`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Generate months for last 2 years
  const now = new Date();
  const monthList = [];
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthList.push({ key, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="financial-controls-page" className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Financial Controls</h1>
        <p className="text-sm text-slate-500 mt-1">Financial year management & month locking</p>
      </div>

      {/* Financial Years */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-orange-600" />
            <h3 className="font-heading font-semibold text-slate-800 text-sm">Financial Years</h3>
          </div>
          <Button size="sm" onClick={() => setCreateDialog(true)} className="bg-orange-600 hover:bg-orange-700 text-white text-xs" data-testid="fc-create-fy">
            <Plus className="w-3.5 h-3.5 mr-1" /> Create Year
          </Button>
        </div>
        <div className="divide-y divide-slate-100">
          {years.length === 0 && <p className="text-center text-slate-400 py-8 text-sm">No financial years created yet</p>}
          {years.map(fy => (
            <div key={fy.fy_id} className="flex items-center justify-between px-5 py-3" data-testid={`fy-${fy.fy_id}`}>
              <div>
                <p className="font-semibold text-sm text-slate-800">{fy.name}</p>
                <p className="text-[10px] text-slate-500">{fy.start_date} to {fy.end_date}</p>
              </div>
              <div className="flex items-center gap-3">
                {fy.is_closed ? (
                  <>
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-full">
                      <Lock className="w-3 h-3" /> Closed
                    </span>
                    <Button variant="outline" size="sm" onClick={() => handleReopenFY(fy)} className="text-xs" data-testid={`fy-reopen-${fy.fy_id}`}>
                      <Unlock className="w-3 h-3 mr-1" /> Reopen
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <Shield className="w-3 h-3" /> Open
                    </span>
                    <Button variant="outline" size="sm" onClick={() => handleCloseFY(fy)} className="text-xs border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`fy-close-${fy.fy_id}`}>
                      <Lock className="w-3 h-3 mr-1" /> Close Year
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Month Locking */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200">
          <Lock className="w-4 h-4 text-orange-600" />
          <h3 className="font-heading font-semibold text-slate-800 text-sm">Month Locking</h3>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4 text-[10px] text-slate-500">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            Locked months prevent all edits, deletions, and backdated entries.
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
            {monthList.map(m => {
              const isLocked = lockMap[m.key];
              return (
                <button key={m.key} onClick={() => toggleMonth(m.key)}
                  className={`p-2.5 rounded-md border text-center transition-all text-xs font-medium ${
                    isLocked
                      ? 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`} data-testid={`month-${m.key}`}>
                  {isLocked ? <Lock className="w-3.5 h-3.5 mx-auto mb-1 text-rose-500" /> : <Unlock className="w-3.5 h-3.5 mx-auto mb-1 text-slate-400" />}
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Create FY Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Financial Year</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="form-label">Start Date (e.g., 2025-04-01)</label>
              <input type="date" value={newFY.start_date} onChange={(e) => setNewFY(f => ({ ...f, start_date: e.target.value }))}
                className="form-input" data-testid="fy-start" />
            </div>
            <div>
              <label className="form-label">End Date (e.g., 2026-03-31)</label>
              <input type="date" value={newFY.end_date} onChange={(e) => setNewFY(f => ({ ...f, end_date: e.target.value }))}
                className="form-input" data-testid="fy-end" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateFY} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="fy-create-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
